import * as React from 'react';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import TitleIcon from '@mui/icons-material/Title';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import DescriptionIcon from '@mui/icons-material/Description';
import PublicIcon from '@mui/icons-material/Public';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router';
import type { Offer } from '../data/offers';
import { getAllCountries, type Country } from '../data/countries';
import {useLanguage} from "../../contexts/LanguageContext";
import { ArrowForward } from '@mui/icons-material';

export interface OfferFormState {
  values: Partial<Omit<Offer, 'id'>> & { images?: File[]; gallery_image_urls?: string[] };
  errors: Partial<Record<keyof OfferFormState['values'], string>> & { images?: string };
}

export type FormFieldValue = string | string[] | number | boolean | File | File[] | null;

export interface OfferFormProps {
  formState: OfferFormState;
  onFieldChange: (
    name: keyof OfferFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<OfferFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<OfferFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function OfferForm(props: OfferFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [countries, setCountries] = React.useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = React.useState(true);

  React.useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countriesData = await getAllCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof OfferFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleNumberFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onFieldChange(
        event.target.name as keyof OfferFormState['values'],
        value === '' ? undefined : Number(value),
      );
    },
    [onFieldChange],
  );

  const handleSelectFieldChange = React.useCallback(
    (event: any) => {
      onFieldChange(
        event.target.name as keyof OfferFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleImageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        const currentImages = (formValues.images as File[]) || [];
        const newImages = [...currentImages, ...Array.from(files)];
        onFieldChange('images', newImages);
      }
    },
    [formValues.images, onFieldChange],
  );

  const handleRemoveExistingImage = React.useCallback(
    (index: number) => {
      const currentUrls = (formValues.gallery_image_urls as string[]) || [];
      const newUrls = currentUrls.filter((_, i) => i !== index);
      onFieldChange('gallery_image_urls', newUrls);
    },
    [formValues.gallery_image_urls, onFieldChange],
  );

  const handleRemoveImage = React.useCallback(
    (index: number) => {
      const currentImages = (formValues.images as File[]) || [];
      const newImages = currentImages.filter((_, i) => i !== index);
      onFieldChange('images', newImages);
    },
    [formValues.images, onFieldChange],
  );

  const handleReset = React.useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = React.useCallback(() => {
    navigate(backButtonPath ?? '/offers');
  }, [navigate, backButtonPath]);
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          {/* Basic Information Section */}
          <Grid size={{ xs: 12 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                mt: 1,
                display: 'flex', 
                alignItems: 'center',
                color: 'primary.main',
                fontWeight: 600
              }}
            >
              <DescriptionIcon sx={{ mr: 1 }} />
              {t('offers.details')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {/* Country Dropdown */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <FormControl
              sx={{ minWidth: 200 }}
              error={!!formErrors.country_id}
              required
            >
              <InputLabel
                id="offer-country-label"
                sx={{
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('offers.country')}
                </Box>
              </InputLabel>
              <Select
                value={formValues.country_id ?? ''}
                onChange={handleSelectFieldChange as SelectProps['onChange']}
                labelId="offer-country-label"
                name="country_id"
                label={t('offers.country')}
                disabled={loadingCountries}
                displayEmpty
                required
                startAdornment={
                  <InputAdornment position="start">
                  </InputAdornment>
                }
                sx={{
                  backgroundColor: 'transparent',
                  textAlign: 'center',
                  '& .MuiSelect-select': {
                    textAlign: 'center'
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em>{t('common.selectCountry')}</em>
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{
                textAlign: isRtl ? 'right' : 'left',
                marginRight: isRtl ? '14px' : 0,
                marginLeft: isRtl ? 0 : '14px'
              }}>{formErrors.country_id ?? ' '}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Seats Number */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.seats_total ?? ''}
              onChange={handleNumberFieldChange}
              name="seats_total"
              label={t('offers.seatsNumber')}
              error={!!formErrors.seats_total}
              helperText={formErrors.seats_total ?? ' '}
              type="number"
              fullWidth
              required
              InputProps={{
                inputProps: { min: 0 }
              }}
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.title ?? ''}
              onChange={handleTextFieldChange}
              name="title"
              label= {t('offers.name')}
              error={!!formErrors.title}
              helperText={formErrors.title ?? ' '}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    right: isRtl ? 0 : 'auto',
                    left: isRtl ? 'auto' : 0,
                    transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

            <Grid size={{ xs: 12 }}>
                <TextField
                    value={formValues.description ?? ''}
                    onChange={handleTextFieldChange}
                    name="description"
                    label={t('offers.salesDescription')}
                    error={!!formErrors.description}
                    helperText={formErrors.description ?? ' '}
                    multiline
                    fullWidth
                    required
                    sx={{
                        '& .MuiInputBase-root': {
                            alignItems: 'flex-start',
                            height: '150px',
                            overflow: 'hidden',
                        },
                        '& .MuiInputBase-input': {
                            height: '150px !important',
                            overflow: 'auto !important',
                            alignItems: 'flex-start',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(0,0,0,0.1)',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '4px',
                                '&:hover': {
                                    background: 'rgba(0,0,0,0.4)',
                                },
                            },
                        }
                    }}
                    InputLabelProps={{
                        sx: {
                            textAlign: isRtl ? 'right' : 'left',
                            right: isRtl ? 0 : 'auto',
                            left: isRtl ? 'auto' : 0,
                            transformOrigin: isRtl ? 'top right' : 'top left'
                        }
                    }}
                    FormHelperTextProps={{
                        sx: {
                          textAlign: isRtl ? 'right' : 'left',
                          marginRight: isRtl ? '14px' : 0,
                          marginLeft: isRtl ? 0 : '14px'
                        }
                    }}
                />
            </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              value={formValues.cost_description ?? ''}
              onChange={handleTextFieldChange}
              name="cost_description"
              label={t('offers.costDescription')}
              error={!!formErrors.cost_description}
              helperText={formErrors.cost_description ?? ' '}
              multiline
              fullWidth
              required
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                  height: '150px',
                  overflow: 'hidden',
                },
                '& .MuiInputBase-input': {
                  height: '150px !important',
                  overflow: 'auto !important',
                  alignItems: 'flex-start',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(0,0,0,0.4)',
                    },
                  },
                }
              }}
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left'
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>


            {/* Provider Information Section */}
            <Grid size={{ xs: 12 }}>
                <Typography
                    variant="h6"
                    sx={{
                        mb: 2,
                        mt: 3,
                        display: 'flex',
                        alignItems: 'center',
                        color: 'primary.main',
                        fontWeight: 600
                    }}
                >
                    <PersonIcon sx={{ mr: 1 }} />
                    {t('offers.providerInformation')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
            </Grid>

            {/* Provider Name */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <TextField
                    value={formValues.provider_name ?? ''}
                    onChange={handleTextFieldChange}
                    name="provider_name"
                    label={t('offers.providerName')}
                    error={!!formErrors.provider_name}
                    helperText={formErrors.provider_name ?? ' '}
                    fullWidth
                    InputLabelProps={{
                        sx: {
                            textAlign: isRtl ? 'right' : 'left',
                            right: isRtl ? 0 : 'auto',
                            left: isRtl ? 'auto' : 0,
                            transformOrigin: isRtl ? 'top right' : 'top left'
                        }
                    }}
                    FormHelperTextProps={{
                        sx: {
                          textAlign: isRtl ? 'right' : 'left',
                          marginRight: isRtl ? '14px' : 0,
                          marginLeft: isRtl ? 0 : '14px'
                        }
                    }}
                />
            </Grid>

            {/* Provider Phone Number */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <TextField
                    value={formValues.provider_phone_number ?? ''}
                    onChange={handleTextFieldChange}
                    name="provider_phone_number"
                    label={t('offers.providerPhoneNumber')}
                    error={!!formErrors.provider_phone_number}
                    helperText={formErrors.provider_phone_number ?? ' '}
                    type="tel"
                    fullWidth
                    InputLabelProps={{
                        sx: {
                            textAlign: isRtl ? 'right' : 'left',
                            right: isRtl ? 0 : 'auto',
                            left: isRtl ? 'auto' : 0,
                            transformOrigin: isRtl ? 'top right' : 'top left'
                        }
                    }}
                    FormHelperTextProps={{
                        sx: {
                          textAlign: isRtl ? 'right' : 'left',
                          marginRight: isRtl ? '14px' : 0,
                          marginLeft: isRtl ? 0 : '14px'
                        }
                    }}
                />
            </Grid>

          {/* Image Upload Section */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ mb: 3 }} />

            <RtlButton
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
              sx={{ 
                mb: 2,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
            >
              {t('offers.addImages')}
              <input
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
            </RtlButton>
            {formErrors.images && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                {formErrors.images}
              </Typography>
            )}

            {/* Existing Images Grid */}
            {formValues.gallery_image_urls && formValues.gallery_image_urls.length > 0 && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {formValues.gallery_image_urls.map((imageUrl, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`existing-${index}`}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="200"
                          image={imageUrl}
                          alt={`Existing ${index + 1}`}
                          sx={{ objectFit: 'cover', marginBottom: '5px', borderRadius: '8px'}}
                        />
                        <CardActions>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveExistingImage(index)}
                            aria-label="delete existing image"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* New Images Grid */}
            {formValues.images && (formValues.images as File[]).length > 0 && (
              <>
                <Grid container spacing={2}>
                  {(formValues.images as File[]).map((image, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`new-${index}`}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="200"
                          image={URL.createObjectURL(image)}
                          alt={`Preview ${index + 1}`}
                          sx={{ objectFit: 'cover' , marginBottom: '5px', borderRadius: '8px' }}
                        />
                        <CardActions>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveImage(index)}
                            aria-label="delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <RtlButton
          type="submit"
          variant="contained"
          size="large"
          loading={isSubmitting}
          sx={{ background: '#028bcb !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#0277a8 !important' } }}
        >
          {submitButtonLabel}
        </RtlButton>
      </Stack>
    </Box>
  );
}

